import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { authAPI } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Film, Eye, EyeOff } from 'lucide-react';
import SocialLoginButtons from '@/components/auth/SocialLoginButtons';
import { getGoogleWebClientId, getAppleWebClientId } from '@/config/runtimeConfig';
import { redirectAfterAuth } from '@/utils/oauth';
import { useRedirectIfAuthenticated } from '@/hooks/useRedirectIfAuthenticated';
import PageSeo from '@/components/seo/PageSeo';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, logout } = useAuth();
  useRedirectIfAuthenticated();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  useEffect(() => {
    const st = location.state;
    if (st?.verifyEmailHint) {
      setInfo(
        st.verifyMessage ||
          'Enviamos um link de confirmação. Abra o e-mail antes de iniciar sessão.'
      );
      if (st.email) setEmail(st.email);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login({ email, password });
      if (isStaffPanelUser(result.user)) {
        logout();
        setError(STAFF_PUBLIC_LOGIN_MESSAGE);
        return;
      }
      redirectAfterAuth(result.user, navigate);
    } catch (err) {
      if (err.code === 'EMAIL_NOT_VERIFIED' && err.email) {
        setEmail(err.email);
      }
      setError(err.message || 'Email ou senha incorretos');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-full flex items-center justify-center py-12 px-4">
      <PageSeo
        title="Entrar | Ufokko"
        description="Acesse sua conta Ufokko."
        path="/login"
        noindex
      />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-md"
      >
        <Card className="shadow-2xl border-0 bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl">
          <CardHeader className="space-y-1 text-center">
            <div className="mx-auto w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mb-4">
              <Film className="w-8 h-8 text-primary" aria-hidden />
            </div>
            <CardTitle className="text-3xl font-bold bg-gradient-to-r from-[#0E76FF] to-[#2563EB] bg-clip-text text-transparent">
              Bem-vindo
            </CardTitle>
            <CardDescription className="text-gray-600 dark:text-gray-400">
              Faça login para acessar sua conta
            </CardDescription>
          </CardHeader>
          <CardContent>
            {(getGoogleWebClientId() || getAppleWebClientId()) ? (
              <>
                <SocialLoginButtons disabled={loading} />
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-gray-200 dark:border-gray-600" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white dark:bg-gray-800 px-2 text-gray-500 dark:text-gray-400">
                      ou com e-mail
                    </span>
                  </div>
                </div>
              </>
            ) : null}
            <form onSubmit={handleSubmit} className="space-y-4">
              {info && (
                <Alert className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
                  <AlertDescription className="text-blue-900 dark:text-blue-100">
                    {info}
                  </AlertDescription>
                </Alert>
              )}
              {error && (
                <Alert variant="destructive" className="bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
                  <AlertDescription className="text-red-800 dark:text-red-200">
                    {error}
                    {error.includes('e-mail') || error.includes('email') ? (
                      <button
                        type="button"
                        className="block mt-2 text-sm underline text-red-900 dark:text-red-100"
                        disabled={resendLoading || !email}
                        onClick={async () => {
                          setResendLoading(true);
                          try {
                            await authAPI.resendVerification(email);
                            setInfo('Se existir conta pendente, enviámos um novo link.');
                            setError('');
                          } catch (e) {
                            setError(e.response?.data?.detail || 'Não foi possível reenviar.');
                          } finally {
                            setResendLoading(false);
                          }
                        }}
                      >
                        {resendLoading ? 'A enviar…' : 'Reenviar e-mail de confirmação'}
                      </button>
                    ) : null}
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-700 dark:text-gray-300">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 focus:border-blue-500 dark:focus:border-blue-400"
                  data-testid="login-email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-gray-700 dark:text-gray-300">
                  Senha
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 focus:border-blue-500 dark:focus:border-blue-400 pr-10"
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/20"
                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-[#0E76FF] to-[#2563EB] hover:from-[#0D6AE6] hover:to-[#1d4ed8] text-white font-semibold py-6 shadow-lg hover:shadow-xl transition-all"
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Entrando...
                  </div>
                ) : (
                  'Entrar'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center space-y-2">
              <Link
                to="/recuperar-senha"
                className="inline-flex items-center justify-center w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                data-testid="login-recuperar-acesso"
              >
                Recuperar acesso
              </Link>
              
              <div className="my-3 border-t border-gray-200 dark:border-gray-700"></div>
              
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Não tem uma conta?{' '}
                <Link
                  to="/cadastro"
                  className="font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                >
                  Cadastre-se
                </Link>
              </p>
              <Link
                to="/"
                className="text-sm text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors block"
              >
                ← Voltar para Home
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Info Footer */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400"
        >
          Login seguro e protegido
        </motion.p>
      </motion.div>
    </div>
  );
};

export default Login;
